import { PACK_REGISTRY, SHIFT_PACK_ORDER, type PackId } from "../content/registry.js";
import { storyComplete, storyStars, storyUnlocked } from "../game/progress.js";
import { storyMotifFor } from "../shell/lib/story-motifs.js";
import type { GameController } from "../shell/hooks/useGameController.js";

/** Source poster for a pack, derived from its id (matches BriefScreen). */
function packPosterSrc(packId: string): string {
  const slug = packId.replace(/^scenario-/, "");
  return `/assets/visual/${slug}-master-poster.jpg`;
}

/**
 * The "Desk" roadmap hub — a dense poster-thumbnail grid (3 per row) that scales
 * to many levels. Each card is a poster with its X/3 stars; the whole card opens
 * the level's Brief. Completed levels read as dimmed "done", locked ones stay
 * dimmed with a follower/star hint. Presentation only; reads progression
 * selectors, never scores. Distinct from the Portfolio; the two cross-link.
 */
export function RoadmapScreen({ game }: { game: GameController }) {
  const { dispatch } = game;

  return (
    <div className="desk-app desk-chrome roadmap-screen" data-testid="screen-roadmap">
      <div className="roadmap-inner">
        <div className="roadmap-topline">
          <button
            type="button"
            className="roadmap-back-btn"
            data-testid="roadmap-home"
            aria-label="Back home"
            onClick={() => dispatch({ type: "CLOSE_ROADMAP" })}
          >
            ‹
          </button>
          <span className="roadmap-title-tag">YOUR DESK</span>
          <button
            type="button"
            className="roadmap-portfolio-link"
            data-testid="roadmap-open-portfolio"
            onClick={() => dispatch({ type: "OPEN_PORTFOLIO" })}
          >
            Reels
          </button>
        </div>

        <h2 className="roadmap-head">Every story, every crowd</h2>
        <p className="roadmap-sub">
          Land all three crowds to complete a story. They want opposite things, so you&apos;ll
          have to re-cut.
        </p>

        <ol className="roadmap-grid" data-testid="roadmap-list">
          {SHIFT_PACK_ORDER.map((packId) => (
            <RoadmapCard
              key={packId}
              packId={packId}
              caption={PACK_REGISTRY[packId].title}
              game={game}
            />
          ))}
        </ol>
      </div>
    </div>
  );
}

function RoadmapCard({
  packId,
  caption,
  game,
}: {
  packId: PackId;
  caption: string;
  game: GameController;
}) {
  const { state, dispatch } = game;
  const entry = PACK_REGISTRY[packId];
  const motif = storyMotifFor(packId);
  const unlocked = storyUnlocked(state, packId);
  const stars = storyStars(state, packId);
  const complete = storyComplete(state, packId);

  return (
    <li
      className={`roadmap-card${unlocked ? "" : " is-locked"}${complete ? " is-complete" : ""}`}
      data-testid={`roadmap-card-${packId}`}
      data-locked={unlocked ? "0" : "1"}
      data-stars={stars}
      data-complete={complete ? "1" : "0"}
      style={{ ["--story-accent" as string]: motif.accent }}
    >
      <button
        type="button"
        className="roadmap-card-open"
        data-testid={`roadmap-open-${packId}`}
        disabled={!unlocked}
        aria-label={`Open ${entry.title}`}
        onClick={() => unlocked && dispatch({ type: "OPEN_STORY", packId })}
      >
        <span className="roadmap-card-poster" aria-hidden>
          <img src={packPosterSrc(packId)} alt="" draggable={false} />
          <span className="roadmap-card-stars" data-testid={`roadmap-stars-${packId}`}>
            {"★".repeat(stars)}
            <span className="roadmap-card-stars-dim">{"★".repeat(3 - stars)}</span>
          </span>
          {!unlocked ? (
            <span className="roadmap-card-locked-band" data-testid={`roadmap-lock-${packId}`}>
              unlock
            </span>
          ) : null}
        </span>
        <span className="roadmap-card-caption" title={caption}>
          {caption}
        </span>
      </button>
    </li>
  );
}
